import React, { useContext, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import apiClient from '../../services/api/apiClient.js';
import { AuthContext } from '../../contexts/AuthContext.jsx';
import DataTable from '../../components/common/DataTable.jsx';
import Pagination from '../../components/common/Pagination.jsx';

/**
 * V1: `IbuFront/src/components/views/Logs/ListadoLogs.jsx` (`/MenuLogs`, gate
 * `menu_logs` -> `bitacora:ver`). Muestra el último mes de la bitácora (el backend ya
 * limita a 30 días, igual que V1), orden por fecha descendente, select "Usuario"
 * (sin admin@sistemify.com) y columnas Fecha / Tipo / Detalle / Usuario. Fecha en
 * `DD-MM-YYYY HH:mm:ss` con offset -06:00 como V1.
 * Diferencia con V1: se pagina de 50 en 50 (V1 pinta todas las filas en una sola tabla).
 */
const POR_PAGINA = 50;

export default function LogsPage() {
  const { hasPermiso } = useContext(AuthContext);
  const puedeVer = hasPermiso('bitacora:ver');

  const [logs, setLogs] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [usuario, setUsuario] = useState('');
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (!puedeVer) return;
    apiClient.get('/logs/usuarios').then(({ data }) => setUsuarios(data.data || [])).catch(() => setUsuarios([]));
  }, [puedeVer]);

  useEffect(() => {
    if (!puedeVer) return;
    setCargando(true);
    setError(null);
    setPage(1);
    apiClient
      .get('/logs', { params: usuario ? { usuario } : {} })
      .then(({ data }) => setLogs(data.data || []))
      .catch((e) => { setError(e.message); setLogs([]); })
      .finally(() => setCargando(false));
  }, [puedeVer, usuario]);

  const filas = useMemo(
    () => logs.map((l) => ({
      ...l,
      fecha: moment(l.createdAt).utcOffset('-0600').format('DD-MM-YYYY HH:mm:ss'),
      nombreUsuario: l.user?.[0] ? `${l.user[0].nombre || ''} ${l.user[0].apellido || ''}`.trim() : '',
    })),
    [logs]
  );

  const totalPages = Math.max(1, Math.ceil(filas.length / POR_PAGINA));
  const pageRows = filas.slice((page - 1) * POR_PAGINA, page * POR_PAGINA);

  if (!puedeVer) return <div className="text-slate-500">No tienes permiso para ver este módulo.</div>;

  const columnas = [
    { key: 'fecha', label: 'Fecha' },
    { key: 'tipo', label: 'Tipo' },
    { key: 'detalle', label: 'Detalle' },
    { key: 'nombreUsuario', label: 'Usuario' },
  ];

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold text-slate-900 text-center">Logs</h1>

      <div className="max-w-xs">
        <h5 className="text-sm font-semibold text-slate-700 mb-2">*Se presenta un mes de información</h5>
        <label className="label">Usuario</label>
        <select value={usuario} onChange={(e) => setUsuario(e.target.value)} className="input-base">
          <option value="">Selecciona</option>
          {usuarios.map((u) => (
            <option key={u._id} value={u._id}>{u.nombre} {u.apellido}</option>
          ))}
        </select>
        {error && <span className="text-sm text-danger-600">{error}</span>}
      </div>

      <DataTable columns={columnas} data={pageRows} loading={cargando} rowKey="_id" />
      <Pagination pagination={{ totalDocs: filas.length, totalPages }} page={page} onChange={setPage} />
    </div>
  );
}
